import React, { useState, useEffect } from 'react'
import { FaArrowRight } from 'react-icons/fa'
import { Link } from 'react-router'
import { Stack, Box, Button, Heading, Text, Stat, List, Loading, SimpleTable, EmptyCard, Badge, Card } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils'
import useStore from '../../store'
import useTitle from '../../hooks/useTitle'
import TrialInsightsBar from './TrialInsightsBar'
import ParticipantJourneyStatsPanel from './ParticipantJourneyStatsPanel'

export default function Home () {
  const [items, setItems] = useState([])
  const [participants, setParticipants] = useState([])
  const [siteBoards, setSiteBoards] = useState([])
  const { user, trial, sites, setSiteUnreads } = useStore()
  const [loading, setLoading] = useState(true)
  useTitle('Overview', trial)

  useEffect(() => {
    setLoading(true)
    api.users.getItems(user._id, d => {
      const newItems = []
      d.tasks?.forEach(t => newItems.push({ type: 'task', ...t }))
      d.queries?.forEach(q => newItems.push({ type: 'query', ...q }))
      d.siteMonitoringIssues?.forEach(s => newItems.push({ type: 'siteMonitoringIssue', ...s }))
      setItems(newItems?.filter(i => i.trial === trial._id).sort((a, b) => new Date(a.dueDate || a.scheduledAt || a.createdAt) - new Date(b.dueDate || b.scheduledAt || b.createdAt)))
      setLoading(false)
    })

    api.trials.getParticipants(trial._id, 1, null, null, false, 'lastSeenAt', 'desc', ({ participants, total }) => {
      setParticipants(participants?.slice(0, 10))
    })
  }, [trial._id])

  // Site message boards: discoverable here for users whose access is scoped to
  // sites (the Sites page itself is hidden from them behind canReadTrialSites).
  // We render only boards the user can read; unread counts come from
  // getUnreadCounts (with name) so this panel doesn't depend on store.sites.
  useEffect(() => {
    if (!trial?._id) return
    // Skip the fetch entirely if the user has no readable boards locally
    // (cheap pre-filter using already-seeded store.sites).
    const hasAnyBoard = (sites || []).some(s => utils.canReadSiteMessages(user, trial, s))
    if (!hasAnyBoard && !utils.canReadTrialMessages(user, trial)) return
    const fetchBoards = () =>
      api.sites.getUnreadCounts(trial._id, d => {
        setSiteBoards(d.boards || [])
        setSiteUnreads(d.sites || {})
      }, () => {})
    fetchBoards()
    const interval = setInterval(fetchBoards, 30_000)
    return () => clearInterval(interval)
  }, [trial._id, sites, user])

  const trialReady = trial && utils.canWriteTrial(user, trial) && trial.name && trial.description && trial.code && trial.dataTypeCount > 0 && trial.stageCount > 0

  const usefulLinks = [
    utils.canManageTrial(user, trial) && { icon: <FaArrowRight />, to: `/trials/${trial._id}/personnel`, content: 'Control who has access to your trial.' },
    utils.canSupportTrial(user, trial) && { icon: <FaArrowRight />, to: `/trials/${trial._id}/messages`, content: 'Provide support to participants.' },
    utils.canManageTrial(user, trial) && { icon: <FaArrowRight />, to: `/trials/${trial._id}/results`, content: 'Set-up an integration to get easy access to data.' },
    { icon: <FaArrowRight />, to: `/trials/${trial._id}/participants`, content: 'View participants in the trial.' }
  ]

  return (
    <Stack direction={{ base: 'column', md: 'row' }} spacing={5}>
      <Stack gap={5} w={{ md: '500px' }}>
        <TrialInsightsBar trialId={trial?._id} />
        <Heading size='lg'>For you</Heading>
        {loading && <Loading />}
        {items.length === 0 && !loading && (trialReady || !utils.canWriteTrial(user, trial)) &&
          <EmptyCard
            icon='tick'
            title='No outstanding to-do items'
            description='You currently have no outstanding items to action.'
          />}
        {items.length > 0 && !loading &&
          <>
            <Stack>
              {items.map(i => {
                let to = `/trials/${trial._id}`
                if (i.type === 'task') to += '/tasks'
                if (i.type === 'query') to += '/queries'
                if (i.type === 'siteMonitoringIssue') to += `/sites/${i.site}/monitoring`
                return (
                  <Card as={Link} to={to} key={i._id} gap={3}>
                    <Box><Badge colorPalette='blue'>Task</Badge></Box>
                    <Heading size='sm'>{i.title || i.name}</Heading>
                    <Text fontSize='sm' color='fg.subtle'>{i.description}</Text>
                  </Card>
                )
              })}
            </Stack>
          </>}

        {!trialReady && utils.canManageTrial(user, trial) &&
          <>
            {!(trial.name && trial.description && trial.code) &&
              <EmptyCard
                icon='info'
                badge='Todo'
                title='Add trial information'
                description="Name and describe your trial so participants know what it's about."
              >
                <Button colorPalette='primary' as={Link} to={`/trials/${trial._id}/info`}>Set-up</Button>
              </EmptyCard>}
            {trial?.dataTypeCount === 0 &&
              <EmptyCard
                icon='data'
                badge='Todo'
                title='Set-Up data types'
                description='Specify the variables that your trial will collect.'
              >
                <Button colorPalette='primary' as={Link} to={`/trials/${trial._id}/data`}>Set-up</Button>
              </EmptyCard>}
            {trial?.stageCount === 0 &&
              <EmptyCard
                icon='form'
                badge='Todo'
                title='Set-Up forms'
                description='Create stages to manage the forms and questionnaires that will describe how to collect your data.'
              >
                <Button colorPalette='primary' as={Link} to={`/trials/${trial._id}/stages`}>Set-up</Button>
              </EmptyCard>}
          </>}
      </Stack>

      <Stack gap={5} maxW='500px'>
        {utils.canReadTrial(user, trial) &&
          <Stack gap={5}>
            <Heading size='lg'>Key information</Heading>
            <Card variant='subtle'>
              <Stack direction='row'>
                <Stat label='Registered participants' value={trial.participantCount || 0}>
                  <Button as={Link} to={`/trials/${trial._id}/participants`} size='sm' colorPalette='blue' variant='outline'>Manage participants</Button>
                </Stat>
                <Stat label='Participant groups' value={trial.participantGroupCount || 0}>
                  <Button as={Link} to={`/trials/${trial._id}/participants`} size='sm' colorPalette='blue' variant='outline'>Manage groups</Button>
                </Stat>
              </Stack>
            </Card>

            <Card variant='subtle'>
              <Stat label='Responses submitted' value={trial.responseCount || 0}>
                <Button as={Link} to={`/trials/${trial._id}/results`} size='sm' colorPalette='blue' variant='outline'>View results</Button>
              </Stat>
            </Card>

            <ParticipantJourneyStatsPanel stats={trial.participantJourneyStats} />
          </Stack>}

        {siteBoards.length > 0 &&
          <Stack gap={3}>
            <Heading size='md'>Site message boards</Heading>
            <Card variant='subtle'>
              <SimpleTable
                tableProps={{ size: 'sm', variant: 'simple' }}
                headers={['Site', '']}
                rows={siteBoards.map(board => [
                  board.name || board.site,
                  <Stack direction='row' justifyContent='end' key={board.site}>
                    {board.count > 0 && <Badge colorPalette='yellow' size='sm'>{board.count} unread</Badge>}
                    <Button size='xs' colorPalette='primary' variant='outline' as={Link} to={`/trials/${trial._id}/sites/${board.site}/messages`}>Open board</Button>
                  </Stack>
                ])}
              />
            </Card>
          </Stack>}

        <Heading size='lg'>Useful links</Heading>
        <List spacing={3} items={usefulLinks.filter(l => l)} />

        {participants.length > 0 &&
          <Stack gap={3}>
            <Stack direction='row' justifyContent='space-between'>
              <Heading size='md'>Recent participants</Heading>
              <Button colorPalette='purple' variant='outline' size='sm' as={Link} to={`/trials/${trial._id}/participants`}>View all</Button>
            </Stack>
            <SimpleTable
              headers={['ID', 'Last seen', 'Actions']}
              rows={participants.map(p => [
                p.id,
                p.lastSeenAt ? new Date(p.lastSeenAt).toLocaleString() : 'Never',
                <Button key={`${p.id}-m`} size='xs' colorPalette='primary' as={Link} to={`/trials/${trial._id}/participants/${p._id}`}>Manage</Button>
              ])}
            />
          </Stack>}
      </Stack>

    </Stack>
  )
}
